/**
 * Backup export/import — §13.3.
 *
 * This is data we did NOT produce: it may have been hand-edited, truncated, or
 * written by a different version. Every import is fully validated.
 *
 * ── Why this is version 1 and not 2 ─────────────────────────────────────────────
 *
 * The Arabic app's v2 format carries `srs` cards and `programs`. Neither exists here,
 * and an importer that silently dropped them would turn "restore my library" into
 * partial data loss with no error. So this is its own format, starting at 1, and an
 * Arabic-app export is REJECTED as `unsupported` rather than half-read.
 *
 * ── Why a one-member discriminated union ────────────────────────────────────────
 *
 * VERSIONS MUST BE A DISCRIMINATED UNION, NEVER A LITERAL. The Arabic app learned this
 * the expensive way: v1 used `z.literal(1)`, which REFUSES any other version, so
 * bumping the format would have rejected every backup already sitting in a user's
 * downloads folder. The union has one member today; the shape is what matters, because
 * it is what makes the second member cheap.
 */
import { z } from 'zod'
import { BACKUP_VERSION } from './constants'
import type { Bookmark, Note, PageState } from './types'

const noteSchema = z.object({
  verseKey: z.string(),
  text: z.string(),
  updatedAt: z.number(),
})

const bookmarkSchema = z.object({
  verseKey: z.string(),
  createdAt: z.number(),
})

const pageStateSchema = z.object({
  page: z.number().int().min(1).max(604),
  read: z.boolean(),
  readOn: z.number().nullable(),
})

const backupV1Schema = z.object({
  version: z.literal(1),
  exportedAt: z.number().optional(),
  notes: z.array(noteSchema).default([]),
  bookmarks: z.array(bookmarkSchema).default([]),
  pages: z.array(pageStateSchema).default([]),
})

const backupSchema = z.discriminatedUnion('version', [backupV1Schema])

export const SUPPORTED_BACKUP_VERSIONS = [1] as const

export interface Backup {
  version: typeof BACKUP_VERSION
  exportedAt: number
  notes: Note[]
  bookmarks: Bookmark[]
  pages: PageState[]
}

/**
 * I3: a failure returns a REASON, not a sentence. The caller needs to know WHICH of the
 * three things went wrong — a composed message cannot be branched on, and the wording
 * belongs to the reader (`apps/web/src/content/backup.ts`).
 */
export type ImportFailure = 'unsupported' | 'invalid' | 'notJson'

export type ImportResult =
  | { ok: true; backup: Backup; upgradedFrom: number | null }
  | { ok: false; reason: ImportFailure; issues: string[] }

/**
 * Return a RESULT, not an exception — a bad import is an ordinary user outcome (wrong
 * file picked, truncated download), and the caller needs the issue list.
 */
export function parseBackup(raw: string): ImportResult {
  let json: unknown
  try {
    json = JSON.parse(raw)
  } catch {
    return { ok: false, reason: 'notJson', issues: [] }
  }

  /**
   * Check the version BEFORE parsing. A discriminated-union miss reports as a generic
   * union failure, and "unsupported version" is by far the most likely reason a file
   * will not load — including the one real case: an export from the Arabic app.
   */
  const version = (json as { version?: unknown } | null)?.version
  if (typeof version !== 'number' || !SUPPORTED_BACKUP_VERSIONS.includes(version as 1)) {
    return { ok: false, reason: 'unsupported', issues: [`version: ${String(version)}`] }
  }

  const parsed = backupSchema.safeParse(json)
  if (!parsed.success) {
    const issues = parsed.error.issues.map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`)
    return { ok: false, reason: 'invalid', issues }
  }

  const data = parsed.data

  return {
    ok: true,
    // Nothing to upgrade from yet. Kept in the result so the caller's "imported, and
    // converted from an older file" branch does not have to be invented later.
    upgradedFrom: null,
    backup: {
      version: BACKUP_VERSION,
      exportedAt: data.exportedAt ?? Date.now(),
      notes: data.notes,
      bookmarks: data.bookmarks,
      pages: data.pages,
    },
  }
}

export function serialiseBackup(
  backup: Omit<Backup, 'version' | 'exportedAt'>,
  now: number,
): string {
  const payload: Backup = { version: BACKUP_VERSION, exportedAt: now, ...backup }
  return JSON.stringify(payload, null, 2)
}

export function backupFilename(now: number): string {
  const d = new Date(now)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `mushaf-en-backup-${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}.json`
}
